import type {
  NotificationActionResult,
  NotificationFeedLiveEvent,
  NotificationFeedReady,
} from '@sharedlib/notification-contract';

export function notificationRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
  );
}

export function notificationKeys(value: Record<string, unknown>, required: string[], optional: string[] = []): boolean {
  return (
    required.every((key) => Object.hasOwn(value, key)) &&
    Object.keys(value).every((key) => required.includes(key) || optional.includes(key))
  );
}

export function notificationUuid(value: unknown): value is string {
  return (
    typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value)
  );
}

export function notificationGeneration(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1;
}

function containsControl(value: string, includeSpace = false): boolean {
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (code <= (includeSpace ? 32 : 31) || code === 127) return true;
  }
  return false;
}

export function notificationCursor(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 512 &&
    !containsControl(value, true) &&
    new TextEncoder().encode(value).length <= 512
  );
}

export function notificationDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) return false;
  const time = Date.parse(value);
  return Number.isFinite(time) && new Date(time).toISOString() === value;
}

function chatUuid(value: unknown): value is string {
  return (
    typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value)
  );
}

/** 010: the thread a reply is in, or null for the channel's own stream. */
function chatLaneOrNull(value: unknown): value is string | null {
  return value === null || chatUuid(value);
}

function label(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= max && !containsControl(value);
}

export function parseNotificationReady(value: unknown): NotificationFeedReady | null {
  if (
    !notificationRecord(value) ||
    !notificationKeys(value, ['version', 'connectionId', 'generation', 'leaseExpiresAt', 'recipients']) ||
    value['version'] !== 1 ||
    !notificationUuid(value['connectionId']) ||
    !notificationGeneration(value['generation']) ||
    !notificationDate(value['leaseExpiresAt']) ||
    !Array.isArray(value['recipients']) ||
    value['recipients'].length < 1 ||
    value['recipients'].length > 100
  )
    return null;
  const recipients: { accountId: string; state: 'pending' }[] = [];
  for (const recipient of value['recipients']) {
    if (
      !notificationRecord(recipient) ||
      !notificationKeys(recipient, ['accountId', 'state']) ||
      !notificationUuid(recipient['accountId']) ||
      recipient['state'] !== 'pending' ||
      recipients.some((item) => item.accountId === recipient['accountId'])
    )
      return null;
    recipients.push({ accountId: recipient['accountId'], state: 'pending' });
  }
  return {
    version: 1,
    connectionId: value['connectionId'],
    generation: value['generation'],
    leaseExpiresAt: value['leaseExpiresAt'],
    recipients,
  };
}

export function parseNotificationLiveEvent(value: unknown): NotificationFeedLiveEvent | null {
  if (
    !notificationRecord(value) ||
    !notificationKeys(
      value,
      [
        'version',
        'generation',
        'accountId',
        'mode',
        'deliveryId',
        'title',
        'occurredAt',
        'expiresAt',
        'policyRevision',
        'sound',
        'cursor',
      ],
      ['contextLabel', 'preview', 'viewKey'],
    ) ||
    value['version'] !== 1 ||
    value['mode'] !== 'live' ||
    !notificationGeneration(value['generation']) ||
    !notificationUuid(value['accountId']) ||
    !notificationUuid(value['deliveryId']) ||
    !label(value['title'], 120) ||
    !notificationDate(value['occurredAt']) ||
    !notificationDate(value['expiresAt']) ||
    typeof value['policyRevision'] !== 'number' ||
    !Number.isSafeInteger(value['policyRevision']) ||
    value['policyRevision'] < 0 ||
    typeof value['sound'] !== 'boolean' ||
    !notificationCursor(value['cursor']) ||
    (Object.hasOwn(value, 'contextLabel') && !label(value['contextLabel'], 120)) ||
    (Object.hasOwn(value, 'preview') && !label(value['preview'], 300)) ||
    (Object.hasOwn(value, 'viewKey') &&
      (typeof value['viewKey'] !== 'string' || !/^chat-v1:[a-f0-9]{64}$/.test(value['viewKey'])))
  )
    return null;
  return {
    version: 1,
    generation: value['generation'],
    accountId: value['accountId'],
    mode: 'live',
    deliveryId: value['deliveryId'],
    title: value['title'],
    occurredAt: value['occurredAt'],
    expiresAt: value['expiresAt'],
    policyRevision: value['policyRevision'],
    sound: value['sound'],
    cursor: value['cursor'],
    ...(typeof value['contextLabel'] === 'string' ? { contextLabel: value['contextLabel'] } : {}),
    ...(typeof value['preview'] === 'string' ? { preview: value['preview'] } : {}),
    ...(typeof value['viewKey'] === 'string' ? { viewKey: value['viewKey'] } : {}),
  };
}

export function parseNotificationAction(value: unknown): NotificationActionResult | null {
  if (!notificationRecord(value) || value['version'] !== 1) return null;
  if (value['status'] === 'denied' || value['status'] === 'unavailable' || value['status'] === 'switch-required') {
    return notificationKeys(value, ['version', 'status']) ? { version: 1, status: value['status'] } : null;
  }
  const target = value['target'];
  if (
    value['status'] !== 'allowed' ||
    !notificationKeys(value, ['version', 'status', 'target']) ||
    !notificationRecord(target) ||
    !notificationKeys(target, ['kind', 'spaceId', 'channelId', 'laneId', 'seq']) ||
    target['kind'] !== 'chat' ||
    !chatUuid(target['spaceId']) ||
    !chatUuid(target['channelId']) ||
    !chatLaneOrNull(target['laneId']) ||
    typeof target['seq'] !== 'number' ||
    !Number.isSafeInteger(target['seq']) ||
    target['seq'] < 1
  )
    return null;
  return {
    version: 1,
    status: 'allowed',
    target: {
      kind: 'chat',
      spaceId: target['spaceId'],
      channelId: target['channelId'],
      laneId: target['laneId'],
      seq: target['seq'],
    },
  };
}
